import { FC, useMemo } from "react";
import { Cell, Legend, Pie, PieChart } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";

type Props = {
  header: string;
  description: string;
  data?: Record<string, number>;
};

type LabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
};

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: LabelProps) => {
  const RADIAN = Math.PI / 180;
  const radius = (innerRadius + outerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={14}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const COLORS = [
  "#2C8C7D", // Deep Teal
  "#E0674B", // Coral Orange
  "#263841", // Charcoal Blue
  "#0088FE", // Vivid Blue
  "#00C49F", // Emerald Green
  "#FFBB28", // Golden Yellow
  "#FF8042", // Bright Orange
  "#A28EFF", // Lavender Purple
  "#FF6FB1", // Pink Rose
  "#34D399", // Fresh Green
  "#F87171", // Soft Red
  "#60A5FA", // Sky Blue
  "#FBBF24", // Amber Yellow
  "#4ADE80", // Mint Green
  "#F472B6", // Rose Pink
  "#2DD4BF", // Aqua Cyan
  "#C084FC", // Light Purple
  "#F59E0B", // Dark Amber
];

export const SkuAnalyticsBreakdown: FC<Props> = ({
  header,
  description,
  data,
}) => {
  const chartData = useMemo(() => {
    if (!data) return [];

    return Object.entries(data).map(([name, value]) => ({
      name,
      value,
    }));
  }, [data]);

  const chartConfig = useMemo<ChartConfig>(() => {
    if (!data) return {};

    return Object.keys(data).reduce((config, key) => {
      config[key] = { label: key };
      return config;
    }, {} as ChartConfig);
  }, [data]);

  const isEmpty = chartData.length === 0;

  return (
    <Card className="flex-1">
      <CardHeader>
        <CardTitle>{header}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="flex items-center justify-center h-[200px] text-gray-500 font-medium text-sm">
            No data available
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <PieChart>
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                isAnimationActive={false}
                label={renderLabel}
                labelLine={false}
              >
                {chartData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend
                verticalAlign="middle"
                iconSize={8}
                iconType="square"
                layout="vertical"
                align="right"
                wrapperStyle={{
                  lineHeight: "30px",
                  fontWeight: 500,
                }}
              />
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
